import Vue from "vue";
import { mapGetters, mapState } from "vuex";

import actionBarComponent from "@/components/child/actionBar/actionBar.vue";
import { Component } from "@/decorators";
import UsuarioModel from "@/models/usuarioModel";
import { IService } from "@/services/base/iService";
import { iServiceCached } from "@/services/base/iServiceCached";
import { AppState, Getters, SessionActions } from "@/store/store";
import { copyObject, getClone } from "@/utils/common/copyObject";
import Delay from "@/utils/common/delay";

import "../crud/crud.scss";

@Component({
    components: {
        actionBarComponent,
    },
    computed: {
        ...mapState({
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
        ...mapGetters(["VALIDAR_PERMISSAO_SOMENTE_CONSULTA", "HAS_PERMISSAO_PERFIL"] as Getters),
    },
})
export default class EditComponentBase<T> extends Vue {
    // State computed props
    usuarioLogado: UsuarioModel;
    VALIDAR_PERMISSAO_SOMENTE_CONSULTA: (telaDescricao: string) => boolean;
    HAS_PERMISSAO_PERFIL: (isRede: boolean, isFranqueador: boolean, isSuporte: boolean) => Promise<boolean>;

    protected service: IService<T> = null;
    protected serviceCached: iServiceCached<T> = null;
    protected tela = null;
    protected newModel = null;

    isModal = false;
    validaFranquiaId = false;
    somenteConsulta = false;
    model: T = null;
    modelId = 0;

    public async setProps(service: IService<T>, tela: string, newModel: T, isModal = false, validaFranquiaId = false) {
        this.service = service;
        this.tela = tela;
        this.newModel = newModel;
        this.model = getClone(newModel);
        this.isModal = isModal;
        this.validaFranquiaId = validaFranquiaId;

        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);
    }

    public async setPropsCached(
        serviceCached: iServiceCached<T>,
        tela: string,
        newModel: T,
        isModal = false,
        validaFranquiaId = false,
    ) {
        this.serviceCached = serviceCached;
        this.tela = tela;
        this.newModel = newModel;
        this.model = getClone(newModel);
        this.isModal = isModal;
        this.validaFranquiaId = validaFranquiaId;

        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);
    }

    public async load(customId = null): Promise<void> {
        await this.waitProps();

        if (!(await this.onPreLoadModel())) {
            return null;
        }

        copyObject(this.newModel, this.model);

        if (this.modelId || customId) {
            try {
                const data = await this.getMethod(customId);

                this.model["updateFrom"](data);
                await this.onLoadModel(data);

                const somenteConsultaSemFranquiaId =
                    this.validaFranquiaId &&
                    !(await this.HAS_PERMISSAO_PERFIL(false, false, false)) &&
                    this.model["franquiaId"] == null;

                this.somenteConsulta =
                    this.VALIDAR_PERMISSAO_SOMENTE_CONSULTA(this.tela) || somenteConsultaSemFranquiaId;

                await this.onAfterLoadModel(data);
            } catch {
                this.$router.back();
            }
        }
    }

    private async waitProps(tentativas = 40) {
        while (!this.service && !this.serviceCached && tentativas-- > 0) {
            await Delay(50);
        }
    }

    public async getMethod(customId = null) {
        if (this.serviceCached) {
            return await this.serviceCached.get(customId ?? this.modelId);
        }

        return await this.service
            .get(customId ?? this.modelId)
            .withLoading()
            .resolveWithJSON<T>();
    }

    public async save() {
        if (!(await this.onPreSave())) {
            return false;
        }

        try {
            const isValid = await this.$validator.validateAll();
            if (isValid) {
                const response = await this.saveMethod();
                const id = Number(response.headers.get("Id"));

                if (response.ok) {
                    await this.onSucessoSave(id);
                    await this.$showInclusaoUpdate(this.model["id"]);
                    if (this.isModal) {
                        this.model["id"] = id;
                        this.$emit("save-ok", this.model);
                    } else {
                        this.$router.back();
                    }
                    return true;
                }
            } else {
                this.$focusErrorField();
            }
        } catch {}
    }

    private async saveMethod() {
        if (this.serviceCached) {
            return await this.serviceCached[!this.model["id"] ? "insert" : "update"](
                this.model,
                this.usuarioLogado.franquiaId,
            );
        }
        return await this.service[!this.model["id"] ? "insert" : "update"](this.model)
            .withLoading()
            .resolveWithResponse();
    }

    private cancel() {
        this.$router.back();
    }

    // Callbacks
    public async onPreLoadModel() {
        return true;
    }

    public async onLoadModel(model: T) {}

    public async onAfterLoadModel(model: T) {}

    public async onPreSave() {
        return true;
    }

    public async onSucessoSave(id?: number) {}

    public async afterMounted() {}

    public async mounted() {
        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);

        if (this.$route.params.id && !this.isModal) {
            this.modelId = +this.$route.params.id;
            this.load();
        }
        this.afterMounted();
    }
}
